"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Card, Typography } from "@/components/ui";

interface Segment {
  title: string;
  image: string;
  imageAlt: string;
  items: string[];
}

interface WhoItsForCarouselProps {
  segments: Segment[];
  checkMarkIcon: string;
}

export function WhoItsForCarousel({ segments, checkMarkIcon }: WhoItsForCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!segments.length) return;

    const intervalId = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % segments.length);
    }, 9000);

    return () => clearInterval(intervalId);
  }, [segments.length]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Desktop / large screens: interactive carousel with image on the right */}
      <div className="hidden lg:grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.3fr)] gap-10 items-center">
        <div className="space-y-4">
          {segments.map((segment, index) => {
            const isActive = index === activeIndex;

            return (
              <Card
                key={segment.title}
                variant="default"
                className={`rounded-2xl border cursor-pointer transition-all duration-200 text-left p-4 ${
                  isActive
                    ? "bg-white border-primary shadow-md"
                    : "bg-white border-gray-200 hover:border-primary/40 hover:shadow-sm"
                }`}
                onClick={() => setActiveIndex(index)}
              >
                <div className="p-2">
                  <Typography
                    variant="h3"
                    as="h3"
                    className={`text-base font-semibold mb-2 ${
                      isActive ? "text-primary" : "text-text-primary"
                    }`}
                  >
                    {segment.title}
                  </Typography>

                  {isActive ? (
                    <ul className="space-y-2">
                      {segment.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-sm text-text-secondary"
                        >
                          <Image
                            src={checkMarkIcon}
                            alt="Included"
                            title="Included"
                            width={14}
                            height={14}
                            className="mt-0.5"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body" as="p" className="text-sm text-text-secondary">
                      {segment.items[0]}
                    </Typography>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {segments[activeIndex] && (
          <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden bg-slate-100">
            <Image
              src={segments[activeIndex].image}
              alt={segments[activeIndex].imageAlt}
              title={segments[activeIndex].imageAlt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 40vw"
            />
          </div>
        )}
      </div>

      {/* Mobile / tablet: all cards open, each with its own image (no carousel / swiper feel) */}
      <div className="lg:hidden">
        <div className="space-y-4">
          {segments.map((segment) => (
            <Card
              key={segment.title}
              variant="default"
              className="rounded-2xl border bg-white text-left p-4"
            >
              <div className="p-2">
                <Typography
                  variant="h3"
                  as="h3"
                  className="text-base font-semibold mb-2 text-text-primary"
                >
                  {segment.title}
                </Typography>

                <ul className="space-y-2">
                  {segment.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-sm text-text-secondary"
                    >
                      <Image
                        src={checkMarkIcon}
                        alt="Included"
                        title="Included"
                        width={14}
                        height={14}
                        className="mt-0.5"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {segment.image && (
                  <div className="mt-4 relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100">
                    <Image
                      src={segment.image}
                      alt={segment.imageAlt}
                      title={segment.imageAlt}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 100vw"
                    />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

